import {
  GetSecretQueryType,
  SetSecretQueryType,
} from "../components/common/QueryTypes";
import axios from "./axios";
import { refreshTokenBeforeRequest } from "./tokenRefresh";

export async function getSecret(
  data: GetSecretQueryType,
): Promise<string | null> {
  try {
    const resp = await axios.get("/upload/get_secret", {
      params: data,
    });
    return resp.data?.secret ? resp.data.secret : null;
  } catch (error) {
    console.error("Error getting secret from Key Vault:", error);
    throw error;
  }
}

export async function setSecret(data: SetSecretQueryType): Promise<any> {
  try {
    const resp = await axios.post("/upload/set_secret", data);
    return resp?.data;
  } catch (err) {
    console.error("Error setting secret in Key Vault", err);
    throw err;
  }
}

/**
 * Fetches a container SAS URL for the project's upload container, through the shared axios
 * client and its interceptor — so a network failure toasts "Network Error" as every other
 * request's does. Used by Media Management to read the container; the upload flow uses
 * `getSasUrlForUpload`, which refreshes the Keycloak token first.
 * @param projectId the project's id
 * @param projectGuid the project's guid
 * @returns the SAS URL, or `null` when the server returned none
 */
export async function getSasUrlForProject(
  projectId: number,
  projectGuid: string,
): Promise<string | null> {
  try {
    const resp = await axios.get("/upload/get_project_sas_url", {
      params: {
        project_id: projectId,
        project_guid: projectGuid,
      },
    });
    return resp.data?.sasUrl ? resp.data.sasUrl : null;
  } catch (error) {
    console.error("Error getting sasUrl:", error);
    throw error;
  }
}

/**
 * The upload flow's SAS fetch (AKL-891): refreshes the Keycloak token, then fetches the
 * project's container SAS. Called at the start of an upload and again whenever the manager
 * replaces a SAS hours later, when the token the page holds may have expired while the
 * machine slept. A failed refresh rejects as any failure of this call does — the manager
 * puts the upload in its stopped state and Resume asks again.
 *
 * Kept apart from `getSasUrlForProject` (review cycle 4): that function is also how Media
 * Management reads the container when a project is chosen, and a refresh that failed
 * offline would have rejected before any request reached the axios interceptor — so that
 * page lost its "Network Error" toast and showed an empty deployment list instead. The
 * pre-flight belongs to the upload flow only.
 * @param projectId the project's id
 * @param projectGuid the project's guid
 * @returns the SAS URL, or `null` when the server returned none
 */
export async function getSasUrlForUpload(
  projectId: number,
  projectGuid: string,
): Promise<string | null> {
  await refreshTokenBeforeRequest();
  return getSasUrlForProject(projectId, projectGuid);
}

export async function getSasUrlsForOrganization(
  organizationId: number,
): Promise<string[] | null> {
  try {
    const resp = await axios.get("/upload/get_organization_sas_urls", {
      params: {
        org_id: organizationId,
      },
    });
    return resp.data?.sasUrls ? resp.data.sasUrls : null;
  } catch (error) {
    console.error("Error getting sasUrls:", error);
    throw error;
  }
}
